import { afterNextRender, Component, computed, DestroyRef, inject, input, InputSignal, Signal, signal, WritableSignal } from '@angular/core';
import { BoxDetail, BoxDetailItem } from '../modal-content';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { formatDateToReadableString } from '../../utils/formatting';
import { Item } from '../../types/common.types';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { ProductsService } from '../../../product/products.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EmptyMessageComponent } from '../../empty-message/empty-message.component';
import { SignalsStoreService } from '../../signals-store.service';
import { tap } from 'rxjs';
import { BundleItems } from '../../../product/product.types';
import { handleImageError, isAddressUpdateRequired } from '../../common/utils';
import { ResolutionService } from '../../resolution.service';

@Component({
  selector: 'app-modal-box-detail',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    EmptyMessageComponent
  ],
  templateUrl: './modal-box-detail.component.html',
  styleUrl: './modal-box-detail.component.scss'
})
export class ModalBoxDetailComponent {

  #activeModal = inject(NgbActiveModal);
  #productsService = inject(ProductsService);
  signalsStoreService = inject(SignalsStoreService);
  #resolutionService = inject(ResolutionService);
  private destroyRef = inject(DestroyRef);

  isMobile = computed(() => this.#resolutionService.isMobile());
  isSmallMobile = computed(() => this.#resolutionService.isSmallMobile());
  isDesktop = computed(() => !this.isMobile() && !this.isSmallMobile());

  publishedBundles: Signal<Item[]> = computed(() => this.#setUpPublishedBundles())
  #publishedBundles$ = toObservable(this.publishedBundles);
  items: WritableSignal<BoxDetailItem[]> = signal([]);

  boxDetail: InputSignal<BoxDetail | null> = input<BoxDetail | null>(null);
  #boxDetail$ = toObservable(this.boxDetail);

  selectedPublishedBundle: WritableSignal<Item | null> = signal(null);

  whatsInsideDisclaimer: WritableSignal<string> = signal('');
  handleImageError = handleImageError;

  constructor() {
    afterNextRender(() => {
      this.#boxDetail$.pipe(
        tap(value => {
          const detail = value?.detail ?? [];
          this.items.set(detail);

          // Some bundles come back with an empty inline `detail`: the API leaves
          // bundle.items empty and exposes the contents on the published bundle
          // instead. Fetch them by id in that case — the same call the dropdown
          // makes — or the modal opens with nothing in it.
          if (detail.length) return;

          const publishedBundleId = value?.publishedBundles?.[0]?.id;
          if (publishedBundleId != null) this.#getSelectedBundleItems(+publishedBundleId);
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();

      this.#publishedBundles$.pipe(
        tap(value => this.selectedPublishedBundle.set(value[0])),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
    })
  }

  #setUpItems(items: BundleItems[]) {

    if (!items || !items.length)
      return this.items.set([]);

    this.items.set(items.map(e => {
      const data = formatDateToReadableString(e.salePriceEndDate || '');
      const mmddyyyyFormat = data.mmddyyyyFormat || '';
      return {
        img: e.img,
        productName: e.name,
        quantity: e.quantity,
        packageName: e.packageName || e.display || '',
        producer: e.producerName || '',
        price: e.price,
        isOnSale: e.isOnSale || false,
        salePriceEndDate: e.salePriceEndDate || '',
        shortDiscountDisclaimer: mmddyyyyFormat ? `Deal available until ${mmddyyyyFormat}.` : '',
        originalPrice: e.originalPrice || 0,
      } as BoxDetailItem
    }));
  }

  #setUpPublishedBundles() {
    const boxDetail = this.boxDetail();
    if (!boxDetail) return [];

    const pbs = boxDetail.publishedBundles?.map(pb => {
      const dateFrom = formatDateToReadableString(pb.dateFrom).mmddyyyyFormat
      const dateEnd = formatDateToReadableString(pb.dateTo).mmddyyyyFormat
      return {
        id: pb.id,
        name: `${dateFrom} to ${dateEnd}`
      } as Item
    }) ?? [];

    return pbs;
  }

  setSelectedPublishedBundle(event: MatSelectChange) {
    const option = this.publishedBundles().find(o => o.id === event.value);
    if (option) {
      this.selectedPublishedBundle.set(option);
      this.#getSelectedBundleItems(+option.id);
    }
  }

  #getSelectedBundleItems(bundleId: number) {
    this.#productsService.getBundelItemsById(bundleId).pipe(tap(res => this.#setUpItems(res)), takeUntilDestroyed(this.destroyRef)).subscribe();
  }


  closeModal() {
    this.#activeModal.close({ addToCart: true });
  }
}
